// src/pages/EmailManager.js
import React, { useState, useEffect, useCallback } from 'react';
import { Mail, Plus, Edit2, Trash2, RefreshCw, CheckCircle, AlertTriangle, Send, Search, X, Save, Eye, EyeOff } from 'lucide-react';
import api from '../utils/api';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';

function AccountModal({ account, clients, onClose, onSaved }) {
  const isEdit = !!account?.id;
  const [f, setF] = useState(isEdit ? { ...account, password: '' } : { email: '', display_name: '', host: '', port: 993, smtp_host: '', smtp_port: 587, username: '', password: '', use_ssl: true, use_tls: true, quota_mb: '', client_id: '', notes: '' });
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const toast = useToast();
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.email || !f.host || !f.username) return toast.error('Email, host și username sunt obligatorii');
    setLoading(true);
    try {
      if (isEdit) { await api.put(`/email/accounts/${account.id}`, f); toast.success('Cont actualizat'); }
      else { await api.post('/email/accounts', f); toast.success('Cont adăugat'); }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.error || 'Eroare'); }
    finally { setLoading(false); }
  };

  const testConn = async () => {
    try {
      const { data } = await api.post('/email/test', { host: f.host, port: f.port, username: f.username, password: f.password, use_ssl: f.use_ssl });
      toast.success(data.message);
    } catch (e) { toast.error('Test eșuat'); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-xl">
        <div className="modal-header"><span className="modal-title"><Mail size={16}/> {isEdit ? account.email : 'Cont Email Nou'}</span><button className="btn-icon" onClick={onClose}><X size={16}/></button></div>
        <div className="modal-body">
          <div className="form-grid form-grid-2">
            <div className="form-group"><label className="form-label">Adresă Email *</label><input className="form-input" value={f.email} onChange={e => set('email', e.target.value)} placeholder="user@domain.com"/></div>
            <div className="form-group"><label className="form-label">Nume afișat</label><input className="form-input" value={f.display_name||''} onChange={e => set('display_name', e.target.value)}/></div>
            <div className="form-group"><label className="form-label">Server IMAP *</label><input className="form-input" value={f.host} onChange={e => set('host', e.target.value)} placeholder="mail.domain.com"/></div>
            <div className="form-group"><label className="form-label">Port IMAP</label><input className="form-input" type="number" value={f.port} onChange={e => set('port', e.target.value)}/></div>
            <div className="form-group"><label className="form-label">Server SMTP</label><input className="form-input" value={f.smtp_host||''} onChange={e => set('smtp_host', e.target.value)}/></div>
            <div className="form-group"><label className="form-label">Port SMTP</label><input className="form-input" type="number" value={f.smtp_port} onChange={e => set('smtp_port', e.target.value)}/></div>
            <div className="form-group"><label className="form-label">Username *</label><input className="form-input" value={f.username} onChange={e => set('username', e.target.value)}/></div>
            <div className="form-group"><label className="form-label">Parolă {isEdit && '(lăsați gol pentru a păstra)'}</label>
              <div style={{position:'relative'}}>
                <input className="form-input" type={showPwd?'text':'password'} value={f.password} onChange={e => set('password', e.target.value)} style={{paddingRight:36}}/>
                <button style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',color:'var(--text-secondary)'}} onClick={() => setShowPwd(p => !p)}>
                  {showPwd ? <EyeOff size={14}/> : <Eye size={14}/>}
                </button>
              </div></div>
            <div className="form-group"><label className="form-label">Cotă (MB)</label><input className="form-input" type="number" value={f.quota_mb||''} onChange={e => set('quota_mb', e.target.value)}/></div>
            <div className="form-group"><label className="form-label">Client</label>
              <select className="form-select" value={f.client_id||''} onChange={e => set('client_id', e.target.value)}>
                <option value="">— Fără client —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.company_name||c.contact_name}</option>)}
              </select></div>
          </div>
          <div style={{display:'flex',gap:12,marginTop:8}}>
            <label style={{display:'flex',alignItems:'center',gap:6,cursor:'pointer',fontSize:13}}><input type="checkbox" checked={!!f.use_ssl} onChange={e => set('use_ssl', e.target.checked)}/> SSL</label>
            <label style={{display:'flex',alignItems:'center',gap:6,cursor:'pointer',fontSize:13}}><input type="checkbox" checked={!!f.use_tls} onChange={e => set('use_tls', e.target.checked)}/> TLS/STARTTLS</label>
          </div>
          <div className="form-group" style={{marginTop:12}}><label className="form-label">Notițe</label><textarea className="form-textarea" value={f.notes||''} onChange={e => set('notes', e.target.value)}/></div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={testConn}>Test Conexiune</button>
          <button className="btn btn-secondary" onClick={onClose}>Anulare</button>
          <button className="btn btn-primary" onClick={save} disabled={loading}>{loading ? <span className="spinner"/> : <><Save size={14}/> {isEdit ? 'Actualizare' : 'Adăugare'}</>}</button>
        </div>
      </div>
    </div>
  );
}

export default function EmailManager() {
  const [list, setList] = useState([]);
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const toast = useToast();
  const { hasRole } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, c, s] = await Promise.all([api.get('/email/accounts', {params:{search:search||undefined}}), api.get('/clients'), api.get('/email/accounts/stats')]);
      setList(e.data); setClients(c.data); setStats(s.data);
    } catch (e) { toast.error('Eroare'); } finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const del = async (id, email) => {
    if (!window.confirm(`Ștergi contul ${email}?`)) return;
    try { await api.delete(`/email/accounts/${id}`); toast.success('Cont șters'); load(); }
    catch (e) { toast.error('Eroare'); }
  };

  return (
    <div className="fade-in">
      <div className="page-header">
        <div><div className="page-title">Email Manager</div><div className="page-subtitle">Gestiune conturi și servere de email</div></div>
        <button className="btn btn-primary" onClick={() => setModal({new:true})}><Plus size={14}/> Cont Nou</button>
      </div>
      {stats && (
        <div className="stats-grid" style={{marginBottom:20}}>
          {[{label:'Total Conturi',value:stats.total,color:'var(--accent)',icon:Mail},{label:'Active',value:stats.active,color:'var(--success)',icon:CheckCircle},{label:'Erori',value:stats.errors,color:'var(--danger)',icon:AlertTriangle},{label:'Cotă Folosită',value:`${stats.used_quota||0} MB`,color:'var(--warning)',icon:Mail}].map(k => (
            <div key={k.label} className="stat-card"><div className="stat-icon" style={{background:`${k.color}20`,marginBottom:10}}><k.icon size={16} color={k.color}/></div><div style={{fontSize:20,fontWeight:800,color:k.color}}>{k.value}</div><div style={{fontSize:12,color:'var(--text-secondary)',marginTop:3}}>{k.label}</div></div>
          ))}
        </div>
      )}
      <div className="card">
        <div style={{display:'flex',gap:10,marginBottom:14}}>
          <div style={{position:'relative',flex:1}}><Search size={14} style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:'var(--text-secondary)'}}/><input className="form-input" style={{paddingLeft:32}} placeholder="Caută email..." value={search} onChange={e => setSearch(e.target.value)}/></div>
          <button className="btn btn-secondary" onClick={load}><RefreshCw size={14}/></button>
        </div>
        {loading ? <div className="loading-center"><span className="spinner"/></div> : (
          <div className="table-wrapper">
            <table>
              <thead><tr><th>EMAIL</th><th>SERVER</th><th>CLIENT</th><th>COTĂ</th><th>STATUS</th><th>ACȚIUNI</th></tr></thead>
              <tbody>
                {list.length===0 && <tr><td colSpan={6} style={{textAlign:'center',padding:30,color:'var(--text-secondary)'}}>Niciun cont</td></tr>}
                {list.map(e => (
                  <tr key={e.id}>
                    <td><div style={{fontWeight:600}}>{e.email}</div>{e.display_name && <div style={{fontSize:11,color:'var(--text-secondary)'}}>{e.display_name}</div>}</td>
                    <td style={{fontFamily:'monospace',fontSize:12}}>{e.host}:{e.port}</td>
                    <td style={{fontSize:13,color:'var(--text-secondary)'}}>{e.company_name||'—'}</td>
                    <td style={{fontSize:12}}>{e.used_mb||0} / {e.quota_mb||'∞'} MB</td>
                    <td><span style={{fontSize:11,fontWeight:600,padding:'2px 8px',borderRadius:10,color:e.status==='active'?'var(--success)':e.status==='error'?'var(--danger)':'var(--text-secondary)',background:e.status==='active'?'#22c55e20':e.status==='error'?'#ef444420':'var(--bg-hover)'}}>{e.status}</span></td>
                    <td>
                      <div style={{display:'flex',gap:4}}>
                        <button className="btn-icon" onClick={() => api.get(`/email/accounts/${e.id}`).then(r => setModal(r.data))}><Edit2 size={13}/></button>
                        {hasRole('superadmin','admin') && <button className="btn-icon" style={{color:'var(--danger)'}} onClick={() => del(e.id, e.email)}><Trash2 size={13}/></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {modal && <AccountModal account={modal.new?null:modal} clients={clients} onClose={() => setModal(null)} onSaved={() => {setModal(null);load();}}/>}
    </div>
  );
}
